/**
 * Which columns the wizard and picker offer (#1144).
 *
 * Which list a column belongs to is FIXED, not user-chosen (PRD §8.3):
 * Position, Notes, and custom fields key on (bill × workspace), everything
 * else on the bill. The bill-number column and the selection checkbox are the
 * row's identity/affordance and are deliberately absent — they cannot be
 * hidden (R12).
 *
 * Catalog colIds must match BillTable's columnDefs colIds exactly, since the
 * view's billColumns are applied through AG Grid column state.
 */

import type { SavedViewColumnEntry } from '../../types/savedViews';
import { WORKSPACE_COL_POSITION, WORKSPACE_COL_NOTES, colIdForCustomField } from '../../types/savedViews';
import type { ActionIconKey } from '../actionIconGlossary';

export interface CatalogColumn {
  colId: string;
  label: string;
  defaultWidth: number;
  /**
   * Set on organization custom fields only. The picker marks these with an
   * asterisk and a "* Custom field" footnote, so a column named by the
   * organization is never mistaken for one Capitol Commons ships.
   */
  isCustomField?: boolean;
}

/** Bill-level columns, in default display order. */
/**
 * The Actions column's selectable buttons (#1144 QA round 10). Labels are the
 * wizard rows; descriptions are the hover text there AND on the cell buttons —
 * one SHORT string per action (they feed aria-labels, so never sentences).
 * glossaryKey ties each action to the app-wide action-icon glossary, which the
 * Actions-header legend renders with the full definition.
 */
export const BILL_ACTIONS: {
  key: import('../../types/savedViews').BillActionKey;
  label: string;
  description: string;
  glossaryKey: ActionIconKey;
}[] = [
  { key: 'bookmark', label: 'Add to workspace', description: 'Add to workspace', glossaryKey: 'track' },
  { key: 'share', label: 'Share', description: 'Copy public link to bill', glossaryKey: 'public-link' },
  { key: 'votePredictions', label: 'Vote predictions', description: 'Vote predictions', glossaryKey: 'vote-predictions' },
];

export const BILL_COLUMN_CATALOG: CatalogColumn[] = [
  { colId: 'subjects', label: 'Subjects', defaultWidth: 200 },
  { colId: 'status', label: 'Status', defaultWidth: 200 },
  { colId: 'introducedDate', label: 'Introduced', defaultWidth: 130 },
  { colId: 'calendarDate', label: 'Calendar Date', defaultWidth: 140 },
  { colId: 'lastAmendment', label: 'Amended', defaultWidth: 140 },
  { colId: 'sponsors', label: 'Sponsors', defaultWidth: 200 },
  { colId: 'houseCommittees', label: 'House Committees', defaultWidth: 170 },
  { colId: 'senateCommittees', label: 'Senate Committees', defaultWidth: 170 },
  { colId: 'actions', label: 'Actions', defaultWidth: 150 },
];

/** Fixed workspace-level columns; custom fields are appended per organization. */
export const WORKSPACE_COLUMN_CATALOG: CatalogColumn[] = [
  { colId: WORKSPACE_COL_POSITION, label: 'Position', defaultWidth: 140 },
  { colId: WORKSPACE_COL_NOTES, label: 'Notes', defaultWidth: 220 },
];

export interface CustomFieldDefinitionLite {
  id: string;
  name: string;
  /** Present when the catalog spans more than one organization. */
  organization_id?: string;
}

/**
 * Custom fields as catalog columns.
 *
 * Someone who administers several organizations builds views that pull from
 * all of them, so the catalog offers every org's fields and says which org each
 * one belongs to — `Priority (AARP)`. The org is named on EVERY custom field
 * once the catalog spans more than one org, not only where two orgs happen to
 * pick the same name: with fields from three orgs in one list, knowing which is
 * which is the point, and an unlabelled row is the ambiguous one.
 *
 * A single-org catalog (nearly every user) stays plain — the qualifier would
 * repeat the same org on every row and say nothing.
 *
 * `orgNames` comes from the workspace list's owner_org_name and the user's own
 * memberships; an org missing from both goes unqualified rather than showing a
 * raw uuid.
 */
export function customFieldCatalog(
  defs: CustomFieldDefinitionLite[],
  orgNames: Record<string, string> = {}
): CatalogColumn[] {
  const spansOrgs =
    new Set(defs.map((d) => d.organization_id).filter(Boolean)).size > 1;

  return defs.map((d) => {
    const orgName = d.organization_id ? orgNames[d.organization_id] : undefined;
    return {
      colId: colIdForCustomField(d.id),
      label: spansOrgs && orgName ? `${d.name} (${orgName})` : d.name,
      defaultWidth: 160,
      isCustomField: true,
    };
  });
}

/** Default entries for a catalog selection, preserving catalog order. */
export function entriesFromSelection(
  catalog: CatalogColumn[],
  selected: ReadonlySet<string>
): SavedViewColumnEntry[] {
  return catalog
    .filter((c) => selected.has(c.colId))
    .map((c) => ({ colId: c.colId, hide: false, width: c.defaultWidth }));
}

/**
 * Prune entries whose colId no longer exists (deleted custom field, renamed
 * standard column). Stored JSON is not typed — validate where it is read
 * (PRD §11), never render an empty column or throw.
 */
export function pruneUnknownColumns(
  entries: SavedViewColumnEntry[],
  known: ReadonlySet<string>
): SavedViewColumnEntry[] {
  return entries.filter((e) => known.has(e.colId));
}
